import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Me } from '@huddle/shared';
import { get, isAuthFailure } from '../lib/api';

interface SessionValue {
  me: Me;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}

/** Convenience: the workspace timezone, needed by every timestamp on screen. */
export function useZone(): string {
  return useSession().me.workspace.timezone;
}

/**
 * 'offline' is deliberately distinct from 'signed-out'. Conflating them is how
 * a valid session ended up staring at a login form it could not submit.
 */
export type SessionState = 'loading' | 'ready' | 'signed-out' | 'offline';

/**
 * Where to go when loading the session fails.
 *
 * Pulled out of the component so the rule is testable on its own. It is worth
 * that much because getting it wrong is not a cosmetic bug: the previous
 * version signed people out on a cold start with no signal, which is precisely
 * the walk-in-cooler case the offline design exists for.
 *
 * The rule: only the *server* can sign you out. Everything else is the network.
 */
export function nextStateOnError(current: SessionState, authFailed: boolean): SessionState {
  if (authFailed) return 'signed-out';
  // Already showing content? Keep it — a blip should not blank the screen.
  if (current === 'ready') return 'ready';
  // Cold start, no answer from the server. Not the same as "not signed in".
  return 'offline';
}

export function SessionProvider({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback: (state: Exclude<SessionState, 'ready'>, retry: () => void) => ReactNode;
}) {
  const [me, setMe] = useState<Me | null>(null);
  const [state, setState] = useState<SessionState>('loading');

  const load = useCallback(async () => {
    try {
      const next = await get<Me>('/me');
      setMe(next);
      setState('ready');
    } catch (error) {
      // Only the server saying "you are not signed in" signs anyone out.
      if (isAuthFailure(error)) {
        setMe(null);
        setState(nextStateOnError(state, true));
        return;
      }

      setState((current) => nextStateOnError(current, false));
    }
  }, []);

  // Retry when the network comes back, so the offline screen resolves itself
  // rather than waiting for the user to guess they should reload.
  useEffect(() => {
    if (state !== 'offline') return;
    const retry = () => void load();
    window.addEventListener('online', retry);
    const timer = window.setInterval(retry, 15_000);
    return () => {
      window.removeEventListener('online', retry);
      clearInterval(timer);
    };
  }, [state, load]);

  useEffect(() => {
    void load();
  }, [load]);

  const value = useMemo(() => (me ? { me, refresh: load } : null), [me, load]);

  if (!value || state !== 'ready') {
    return <>{fallback(state === 'ready' ? 'loading' : state, () => void load())}</>;
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
